/**
 * ChatPanel — lightweight community chat, centered on conversation rather
 * than a Discord-style feature set. Local mock messages only.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Send, BookMarked, X } from 'lucide-react';
import { cx } from '@/components/site/primitives';
import { useCommunities } from './context/CommunitiesContext';
import { timeAgo } from './utils/communityHelpers';
import { CURRENT_USER_ID } from './constants/seedData';
import { COMMUNITIES } from '@/constants/testIds';

const avatarTone = { coral: 'bg-coral', yellow: 'bg-yellow', sage: 'bg-sage', blue: 'bg-blue' };

export default function ChatPanel({ isOpen, onClose, community }) {
  const { getChat, sendChatMessage, getMembers } = useCommunities();
  const [text, setText] = useState('');
  const endRef = useRef(null);
  const messages = community ? getChat(community.id) : [];
  const online = community ? getMembers(community.id).filter(m => m.id !== CURRENT_USER_ID).slice(0, 4) : [];

  useEffect(() => {
    if (isOpen) endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, messages.length]);

  if (!community) return null;

  const handleSend = () => {
    if (!text.trim()) return;
    sendChatMessage(community.id, text.trim());
    setText('');
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 border-line bg-card p-0 text-ink sm:max-w-sm" data-testid={COMMUNITIES.chatPanel}>
        <SheetTitle className="sr-only">{community.name} Chat</SheetTitle>
        <div className="flex items-center justify-between border-b border-line p-4">
          <div>
            <p className="font-display text-base font-semibold text-ink">{community.name} Chat</p>
            <p className="text-xs text-subtle">{online.length} members online</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-subtle hover:bg-cream hover:text-ink" aria-label="Close chat"><X className="h-4 w-4" /></button>
        </div>

        {online.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto border-b border-line px-4 py-3">
            {online.map(m => (
              <div key={m.id} className="flex shrink-0 flex-col items-center gap-1">
                <span className={cx('relative grid h-9 w-9 place-items-center rounded-full text-xs font-bold text-[#FFF7F0]', avatarTone[m.tone] ?? avatarTone.coral)}>
                  {m.initial}
                  <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-card bg-sage" />
                </span>
                <span className="max-w-[52px] truncate text-[10px] text-subtle">{m.name}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {messages.length === 0 && <p className="text-center text-sm text-subtle">No messages yet — say hi 👋</p>}
          {messages.map(m => {
            const isMe = m.author.name === 'Sakshi';
            return (
              <div key={m.id} className={cx('flex items-end gap-2', isMe && 'flex-row-reverse')}>
                <span className={cx('grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-bold text-[#FFF7F0]', avatarTone[m.author.tone] ?? avatarTone.coral)}>{m.author.initial}</span>
                <div className={cx('max-w-[75%] rounded-2xl px-3.5 py-2', isMe ? 'rounded-br-md bg-coral-fill text-[#FFF7F0]' : 'rounded-bl-md bg-cream text-ink')}>
                  {!isMe && <p className="text-[11px] font-semibold opacity-80">{m.author.name}</p>}
                  <p className="text-sm">{m.text}</p>
                  <p className={cx('mt-0.5 text-[10px]', isMe ? 'text-[#FFF7F0]/70' : 'text-subtle')}>{timeAgo(m.createdAt)}</p>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <div className="flex items-center gap-2 border-t border-line p-3">
          <button
            type="button"
            onClick={() => setText(t => `${t}${t ? ' ' : ''}📖 `)}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-subtle hover:bg-cream hover:text-coral"
            aria-label="Share a book"
            title="Share a book"
          >
            <BookMarked className="h-4 w-4" />
          </button>
          <input
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            placeholder="Message the community…"
            className="flex-1 rounded-full border border-line bg-cream px-4 py-2 text-sm text-ink outline-none focus:border-coral"
          />
          <button type="button" onClick={handleSend} className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-coral-fill text-[#FFF7F0]" aria-label="Send message">
            <Send className="h-3.5 w-3.5" />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
